import { useEffect, useRef, useState } from 'react';

export const clamp = value => Math.max(0, Math.min(1, value));
export const smooth = (start, end, value) => {
  const t = clamp((value - start) / (end - start));
  return t * t * (3 - 2 * t);
};
export function scrollProgress(top, height, stageHeight, inset = 0) {
  return clamp((inset - top) / Math.max(1, height - stageHeight));
}

// Reserve space from the actual copy, rather than covering the scene with a panel.
export function measureJourney(surface, section) {
  const height = surface.clientHeight;
  const header = document.querySelector('.voices-header')?.clientHeight || 64;
  if (surface.clientWidth >= 800) {
    if (!['hero', 'craft'].includes(section)) return {};
    const copies = Array.from(surface.querySelectorAll('.vj-story-chapter, .vj-craft-copy'));
    if (!copies.length) return {};
    return { readable: {
      left: Math.max(...copies.map(copy => copy.offsetLeft + copy.offsetWidth)) + 32,
      right: surface.clientWidth - 24,
      top: header + 80,
      bottom: height - 100,
    } };
  }
  if (section === 'hero') {
    const title = surface.querySelector('.vj-opening-title');
    const baseline = surface.querySelector('.vj-baseline');
    if (!title || !baseline) return {};
    const copyHeight = Math.max(0, ...Array.from(surface.querySelectorAll('.vj-story-chapter'), el => el.offsetHeight));
    const start = title.offsetTop + title.offsetHeight + 12;
    const end = baseline.offsetTop - 12;
    const copyStart = height - copyHeight - 28;
    const visualStart = header + 20;
    const visualEnd = Math.max(visualStart + 86, copyStart - 20);
    surface.style.setProperty('--vj-mask-start', `${copyStart - 28}px`);
    surface.style.setProperty('--vj-mask-end', `${copyStart + 22}px`);
    return { home: { introCenter: (start + end) / 2 / height, introHeight: Math.max(105, end - start), center: (visualStart + visualEnd) / 2 / height, height: visualEnd - visualStart } };
  }
  if (section === 'craft') {
    const copyHeight = Math.max(210, ...Array.from(surface.querySelectorAll('.vj-craft-copy'), el => el.scrollHeight));
    const heading = surface.querySelector('.vj-craft-heading');
    surface.style.setProperty('--vj-copy-height', `${Math.ceil(copyHeight)}px`);
    const start = (heading?.offsetTop || header) + (heading?.offsetHeight || 44) + 8;
    const end = height - copyHeight - 100;
    return { craft: { center: (start + Math.max(start + 85, end)) / 2 / height, height: Math.max(85, end - start) } };
  }
  return {};
}

export function useMotionPreference() {
  const [override, setOverride] = useState(() => {
    try {
      const saved = sessionStorage.getItem('voices-motion');
      return saved === 'reduce' ? true : saved === 'full' ? false : null;
    } catch { return null; }
  });
  const [system, setSystem] = useState(() => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false);
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const change = () => setSystem(query.matches);
    query?.addEventListener?.('change', change);
    return () => query?.removeEventListener?.('change', change);
  }, []);
  useEffect(() => {
    const change = event => { if (typeof event.detail === 'boolean') setOverride(event.detail); };
    window.addEventListener('voices:motion-change', change);
    return () => window.removeEventListener('voices:motion-change', change);
  }, []);
  const reduced = override ?? system;
  const toggle = () => {
    const next = !reduced;
    setOverride(next);
    try { sessionStorage.setItem('voices-motion', next ? 'reduce' : 'full'); } catch { /* Motion controls also work without storage. */ }
    window.dispatchEvent(new CustomEvent('voices:motion-change', { detail: next }));
  };
  return [reduced, toggle];
}

// Native scrolling remains in charge. GPU work stops when the section is hidden or settled.
export default function useScrollScene({ section, reduced, onFrame }) {
  const root = useRef(null);
  const stage = useRef(null);
  const canvas = useRef(null);
  const callback = useRef(onFrame);
  callback.current = onFrame;
  const [status, setStatus] = useState('pending');
  const [flow, setFlow] = useState(false);
  const active = !reduced && !flow && status !== 'unavailable';

  useEffect(() => {
    const measure = () => {
      const compact = window.innerHeight < 460 || (window.innerWidth > window.innerHeight && window.innerHeight <= 540);
      const copies = stage.current?.querySelectorAll('.vj-story-chapter, .vj-craft-copy') || [];
      const tallest = Math.max(0, ...Array.from(copies, copy => copy.scrollHeight));
      const header = document.querySelector('.voices-header')?.clientHeight || 64;
      setFlow(compact || (window.innerWidth < 800 && tallest > window.innerHeight - header - 80));
    };
    window.addEventListener('resize', measure, { passive: true });
    measure();
    return () => window.removeEventListener('resize', measure);
  }, []);

  useEffect(() => {
    if (reduced || flow) { setStatus('pending'); return; }
    const element = root.current;
    const surface = stage.current;
    const targetCanvas = canvas.current;
    if (!element || !surface || !targetCanvas) return;
    let scene;
    let disposed = false;
    let failed = false;
    let visible = false;
    let loading = false;
    let frame = 0;
    let previousTime = 0;
    let progress;
    let width = 0;
    let height = 0;

    const fail = () => {
      if (disposed || failed) return;
      failed = true;
      cancelAnimationFrame(frame);
      frame = 0;
      scene?.dispose();
      scene = null;
      if (!disposed) setStatus('unavailable');
    };
    const render = time => {
      frame = 0;
      if (disposed || !visible || document.hidden || !scene) return;
      const rect = element.getBoundingClientRect();
      const nextWidth = surface.clientWidth;
      const nextHeight = surface.clientHeight;
      if (!nextWidth || !nextHeight) return;
      if (nextWidth !== width || nextHeight !== height) {
        width = nextWidth; height = nextHeight;
        try { scene.resize(width, height); } catch { fail(); return; }
      }
      const inset = parseFloat(getComputedStyle(surface).top) || 0;
      const target = scrollProgress(rect.top, rect.height, height, inset);
      const dt = Math.min(.064, (time - previousTime) / 1000 || .016);
      previousTime = time;
      progress = progress == null ? target : progress + (target - progress) * (1 - Math.exp(-dt * 12));
      if (Math.abs(target - progress) < .00005) progress = target;
      try {
        const composition = measureJourney(surface, section);
        const result = scene.render({ section, progress, width, height, time: 0, pointer: [0, 0], composition });
        targetCanvas.dataset.progress = progress.toFixed(4);
        callback.current?.(progress, { width, height, ...result });
      } catch { fail(); return; }
      if (progress !== target) frame = requestAnimationFrame(render);
    };
    const wake = () => {
      if (!frame && scene && visible && !document.hidden) frame = requestAnimationFrame(render);
    };
    const load = async () => {
      if (loading || scene || disposed || failed) return;
      loading = true;
      try {
        const { createVoicesScene } = await import('./three/VoicesScene');
        if (disposed || failed) return;
        scene = createVoicesScene(targetCanvas);
        setStatus('ready');
        wake();
      } catch { fail(); }
    };
    const intersection = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting;
      if (visible) { load(); wake(); }
      else { cancelAnimationFrame(frame); frame = 0; }
    }, { rootMargin: '200px 0px' }) : null;
    intersection?.observe(element);
    if (!intersection) { visible = true; load(); }
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(wake) : null;
    resize?.observe(surface);
    surface.querySelectorAll('.vj-chapter, .vj-craft-copy').forEach(copy => resize?.observe(copy));
    const contextLost = event => { event.preventDefault(); fail(); };
    const startupTimeout = () => { if (!targetCanvas.hasAttribute('data-progress')) fail(); };
    targetCanvas.addEventListener('webglcontextlost', contextLost);
    targetCanvas.addEventListener('voicesstartuptimeout', startupTimeout);
    window.addEventListener('scroll', wake, { passive: true });
    window.addEventListener('resize', wake, { passive: true });
    document.addEventListener('visibilitychange', wake);
    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      intersection?.disconnect(); resize?.disconnect();
      targetCanvas.removeEventListener('webglcontextlost', contextLost);
      targetCanvas.removeEventListener('voicesstartuptimeout', startupTimeout);
      window.removeEventListener('scroll', wake);
      window.removeEventListener('resize', wake);
      document.removeEventListener('visibilitychange', wake);
      scene?.dispose();
    };
  }, [reduced, flow, section]);

  const seek = progress => {
    if (!root.current || !stage.current) return;
    const rect = root.current.getBoundingClientRect();
    const inset = parseFloat(getComputedStyle(stage.current).top) || 0;
    window.scrollTo({ top: window.scrollY + rect.top - inset + clamp(progress) * (rect.height - stage.current.clientHeight), behavior: 'instant' });
  };
  return { root, stage, canvas, active, status, seek };
}
